import React, { useEffect, useMemo, useRef, useState } from "react";
import "./styles/ButtonsSequence.css";

const STORAGE_PREFIX = "buttonSequenceData_";
const FAVORITES_KEY = "buttonSequenceFavorites";

function waitTimer(delay) {
    return new Promise((resolve) => setTimeout(resolve, delay));
}

function createStep() {
    return {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        selectedButton: "",
        delay: 150,
    };
}

function ButtonsSequence({ CallFunction, data, selectedRemote }) {
    const [buttonGroups, setButtonGroups] = useState([createStep()]);
    const [message, setMessage] = useState("");
    const [allSavedSequences, setAllSavedSequences] = useState([]);
    const [saveSequenceName, setSaveSequenceName] = useState("");
    const [showDetails, setShowDetails] = useState(false);

    const [isRunning, setIsRunning] = useState(false);
    const [activeStepIndex, setActiveStepIndex] = useState(-1);
    const [favoriteSequences, setFavoriteSequences] = useState([]);
    const stopExecutionRef = useRef(false);

    const selectedRemoteKey = `${selectedRemote}_lirc`;
    const selectedRemoteData = data?.[selectedRemoteKey];

    const availableButtons = useMemo(() => {
        if (!selectedRemoteData) return [];

        return Object.keys(selectedRemoteData)
            .filter(
                (key) => key.startsWith("button_") && !key.includes("_times")
            )
            .map((button) => ({
                value: button,
                label: `${button}: "${selectedRemoteData[button]}"`,
                command: selectedRemoteData[button],
            }));
    }, [selectedRemoteData]);

    const getAllSavedSequences = () => {
        const saved = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(STORAGE_PREFIX)) {
                const value = localStorage.getItem(key);
                saved.push({ key, value });
            }
        }

        saved.sort((a, b) => a.key.localeCompare(b.key));
        setAllSavedSequences(saved);
    };

    const loadFavorites = () => {
        const raw = localStorage.getItem(FAVORITES_KEY);
        if (!raw) {
            setFavoriteSequences([]);
            return;
        }

        try {
            const parsed = JSON.parse(raw);
            setFavoriteSequences(Array.isArray(parsed) ? parsed : []);
        } catch {
            setFavoriteSequences([]);
        }
    };

    const saveFavorites = (favorites) => {
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
        setFavoriteSequences(favorites);
    };

    useEffect(() => {
        getAllSavedSequences();
        loadFavorites();
    }, []);

    const updateStep = (index, patch) => {
        setButtonGroups((prev) =>
            prev.map((step, i) => (i === index ? { ...step, ...patch } : step))
        );
    };

    const addStepToEnd = () => {
        setButtonGroups((prev) => [...prev, createStep()]);
    };

    const insertStepAt = (index) => {
        const next = [...buttonGroups];
        next.splice(index + 1, 0, createStep());
        setButtonGroups(next);
    };

    const duplicateStepAt = (index) => {
        const step = buttonGroups[index];
        if (!step) return;

        const copy = {
            ...step,
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        };

        const next = [...buttonGroups];
        next.splice(index + 1, 0, copy);
        setButtonGroups(next);
    };

    const removeStepAt = (index) => {
        if (buttonGroups.length === 1) {
            setButtonGroups([createStep()]);
            return;
        }

        setButtonGroups((prev) => prev.filter((_, i) => i !== index));
    };

    const moveStep = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= buttonGroups.length) return;

        const next = [...buttonGroups];
        [next[index], next[newIndex]] = [next[newIndex], next[index]];
        setButtonGroups(next);
    };

    const clearEditor = () => {
        stopExecutionRef.current = true;
        setIsRunning(false);
        setActiveStepIndex(-1);
        setButtonGroups([createStep()]);
        setMessage("Sequence editor cleared.");
    };

    const selectSequence = (storageKey) => {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return;

        const parsed = JSON.parse(raw);
        const steps =
            Array.isArray(parsed.buttonGroups) && parsed.buttonGroups.length
                ? parsed.buttonGroups.map((step) => ({
                      id:
                          step.id ||
                          `${Date.now()}_${Math.random()
                              .toString(36)
                              .slice(2, 9)}`,
                      selectedButton: step.selectedButton || "",
                      delay: Number.isFinite(Number(step.delay))
                          ? Number(step.delay)
                          : 150,
                  }))
                : [createStep()];

        setSaveSequenceName(storageKey.replace(STORAGE_PREFIX, ""));
        setButtonGroups(steps);
        setMessage(
            `Loaded sequence "${storageKey.replace(STORAGE_PREFIX, "")}".`
        );
    };

    const saveSequence = () => {
        const cleanName = saveSequenceName.trim().replace(/\s+/g, "_");

        if (!cleanName) {
            setMessage("Please enter a valid sequence name.");
            return;
        }

        const validSteps = buttonGroups.filter((step) => step.selectedButton);
        if (!validSteps.length) {
            setMessage("Add at least one valid step before saving.");
            return;
        }

        localStorage.setItem(
            `${STORAGE_PREFIX}${cleanName}`,
            JSON.stringify({ buttonGroups: validSteps })
        );

        setSaveSequenceName(cleanName);
        getAllSavedSequences();
        setMessage(`Sequence "${cleanName}" saved.`);
    };

    const deleteSequence = () => {
        const cleanName = saveSequenceName.trim();
        if (!cleanName) {
            setMessage("Select a sequence first.");
            return;
        }

        localStorage.removeItem(`${STORAGE_PREFIX}${cleanName}`);

        const nextFavorites = favoriteSequences.filter(
            (item) => item !== cleanName
        );
        saveFavorites(nextFavorites);

        getAllSavedSequences();
        setButtonGroups([createStep()]);
        setSaveSequenceName("");
        setMessage(`Sequence "${cleanName}" deleted.`);
    };

    const toggleFavorite = () => {
        const cleanName = saveSequenceName.trim();
        if (!cleanName) {
            setMessage("Select or save a sequence first.");
            return;
        }

        const isFavorite = favoriteSequences.includes(cleanName);

        if (isFavorite) {
            const next = favoriteSequences.filter((item) => item !== cleanName);
            saveFavorites(next);
            setMessage(`Removed "${cleanName}" from favorites.`);
        } else {
            const next = [...favoriteSequences, cleanName];
            saveFavorites(next);
            setMessage(`Added "${cleanName}" to favorites.`);
        }
    };

    const stopSequence = () => {
        stopExecutionRef.current = true;
        setMessage("Stopping sequence...");
    };

    const executeSequence = async () => {
        if (!selectedRemoteData) {
            setMessage("Please select a remote first.");
            return;
        }

        const stepsToRun = buttonGroups.filter((step) => step.selectedButton);

        if (!stepsToRun.length) {
            setMessage("No valid steps to run.");
            return;
        }

        stopExecutionRef.current = false;
        setIsRunning(true);
        setActiveStepIndex(-1);

        try {
            for (let i = 0; i < stepsToRun.length; i++) {
                if (stopExecutionRef.current) {
                    setMessage("Sequence stopped.");
                    break;
                }

                const step = stepsToRun[i];
                const buttonValue = selectedRemoteData[step.selectedButton];

                if (!buttonValue) {
                    setMessage(`Skipped step ${i + 1} - button not found.`);
                    continue;
                }

                setActiveStepIndex(
                    buttonGroups.findIndex((item) => item.id === step.id)
                );
                setMessage(
                    `Running ${saveSequenceName || "sequence"} - step ${i + 1}/${stepsToRun.length}: ${buttonValue}`
                );

                await CallFunction(buttonValue);
                await waitTimer(Number(step.delay) || 0);
            }

            if (!stopExecutionRef.current) {
                setMessage(
                    `${saveSequenceName || "Sequence"} completed successfully.`
                );
            }
        } catch (error) {
            console.error(error);
            setMessage("Sequence failed during execution.");
        } finally {
            setIsRunning(false);
            setActiveStepIndex(-1);
            stopExecutionRef.current = false;
        }
    };

    const totalDelay = buttonGroups.reduce(
        (sum, step) => sum + (Number(step.delay) || 0),
        0
    );

    const isCurrentFavorite = favoriteSequences.includes(saveSequenceName);

    return (
        <div className="sequence-shell">
            <div className="sequence-panel">
                <div className="sequence-topbar">
                    <div>
                        <p className="sequence-eyebrow">QA Sequence Control</p>
                        <h3 className="sequence-title">Macro Builder</h3>
                        <p className="sequence-subtitle">
                            Remote: <strong>{selectedRemote || "Not selected"}</strong>
                        </p>
                    </div>

                    <button
                        className="sequence-toggle"
                        onClick={() => setShowDetails((prev) => !prev)}
                    >
                        {showDetails ? "Hide Details" : "See Details"}
                    </button>
                </div>

                <div className="sequence-stats">
                    <div className="sequence-stat-card">
                        <span>Saved</span>
                        <strong>{allSavedSequences.length}</strong>
                    </div>
                    <div className="sequence-stat-card">
                        <span>Favorites</span>
                        <strong>{favoriteSequences.length}</strong>
                    </div>
                    <div className="sequence-stat-card">
                        <span>Steps</span>
                        <strong>{buttonGroups.length}</strong>
                    </div>
                    <div className="sequence-stat-card">
                        <span>Status</span>
                        <strong className={isRunning ? "live" : ""}>
                            {isRunning ? "Running" : "Idle"}
                        </strong>
                    </div>
                </div>

                <div className="sequence-quick-run">
                    <label className="sequence-section-title">
                        Favorite Sequences - Quick Run
                    </label>

                    <div className="sequence-chip-list">
                        {favoriteSequences.length ? (
                            favoriteSequences.map((name) => (
                                <button
                                    key={name}
                                    className={`sequence-chip ${
                                        saveSequenceName === name ? "active" : ""
                                    }`}
                                    onClick={() =>
                                        selectSequence(`${STORAGE_PREFIX}${name}`)
                                    }
                                    disabled={isRunning}
                                >
                                    {name}
                                </button>
                            ))
                        ) : (
                            <p className="sequence-empty">
                                No favorites yet. Save a sequence and mark it favorite.
                            </p>
                        )}
                    </div>

                    <div className="sequence-runbar compact">
                        <button
                            className="sequence-btn execute"
                            onClick={executeSequence}
                            disabled={isRunning || !selectedRemote || !saveSequenceName}
                        >
                            Run
                        </button>

                        <button
                            className="sequence-btn stop"
                            onClick={stopSequence}
                            disabled={!isRunning}
                        >
                            Stop
                        </button>

                        <div className="sequence-selected-pill">
                            Selected: {saveSequenceName || "None"}
                        </div>
                    </div>

                    <p className="sequence-message">{message}</p>
                </div>

                {showDetails && (
                    <>
                        <div className="sequence-library">
                            <label className="sequence-section-title">
                                All Saved Sequences
                            </label>

                            <div className="sequence-chip-list">
                                {allSavedSequences.length ? (
                                    allSavedSequences.map((item) => {
                                        const name = item.key.replace(STORAGE_PREFIX, "");
                                        const isActive = name === saveSequenceName;

                                        return (
                                            <button
                                                key={item.key}
                                                className={`sequence-chip ${
                                                    isActive ? "active" : ""
                                                }`}
                                                onClick={() => selectSequence(item.key)}
                                                disabled={isRunning}
                                            >
                                                {name}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <p className="sequence-empty">
                                        No saved sequences yet.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="sequence-save-row">
                            <input
                                type="text"
                                value={saveSequenceName}
                                placeholder="sequence_name"
                                onChange={(e) =>
                                    setSaveSequenceName(
                                        e.target.value.replace(/\s+/g, "_")
                                    )
                                }
                                disabled={isRunning}
                            />

                            <button
                                className="sequence-btn primary"
                                onClick={saveSequence}
                                disabled={isRunning}
                            >
                                Save
                            </button>

                            <button
                                className={`sequence-btn ${
                                    isCurrentFavorite ? "warning" : "ghost"
                                }`}
                                onClick={toggleFavorite}
                                disabled={isRunning || !saveSequenceName}
                            >
                                {isCurrentFavorite
                                    ? "Unfavorite"
                                    : "Favorite"}
                            </button>

                            <button
                                className="sequence-btn danger"
                                onClick={deleteSequence}
                                disabled={isRunning || !saveSequenceName}
                            >
                                Delete
                            </button>

                            <button
                                className="sequence-btn ghost"
                                onClick={clearEditor}
                                disabled={isRunning}
                            >
                                Clear
                            </button>
                        </div>

                        <div className="sequence-runbar">
                            <button
                                className="sequence-btn execute"
                                onClick={executeSequence}
                                disabled={isRunning || !selectedRemote}
                            >
                                Run Sequence
                            </button>

                            <button
                                className="sequence-btn stop"
                                onClick={stopSequence}
                                disabled={!isRunning}
                            >
                                Stop
                            </button>

                            <button
                                className="sequence-btn ghost"
                                onClick={addStepToEnd}
                                disabled={isRunning}
                            >
                                + Add Step
                            </button>

                            <div className="sequence-total-delay">
                                Total Delay: {totalDelay} ms
                            </div>
                        </div>

                        <div className="sequence-steps">
                            {buttonGroups.map((group, index) => {
                                const buttonPreview =
                                    selectedRemoteData?.[group.selectedButton];

                                return (
                                    <div
                                        key={group.id}
                                        className={`sequence-step-card ${
                                            activeStepIndex === index ? "running" : ""
                                        }`}
                                    >
                                        <div className="sequence-step-header">
                                            <div>
                                                <span className="sequence-step-number">
                                                    Step {index + 1}
                                                </span>
                                                <p className="sequence-step-preview">
                                                    {buttonPreview || "No button selected"}
                                                </p>
                                            </div>

                                            <div className="sequence-step-actions">
                                                <button
                                                    onClick={() => moveStep(index, -1)}
                                                    disabled={isRunning || index === 0}
                                                >
                                                    ↑
                                                </button>
                                                <button
                                                    onClick={() => moveStep(index, 1)}
                                                    disabled={
                                                        isRunning ||
                                                        index === buttonGroups.length - 1
                                                    }
                                                >
                                                    ↓
                                                </button>
                                                <button
                                                    onClick={() => insertStepAt(index)}
                                                    disabled={isRunning}
                                                >
                                                    +
                                                </button>
                                                <button
                                                    onClick={() => duplicateStepAt(index)}
                                                    disabled={isRunning}
                                                >
                                                    Copy
                                                </button>
                                                <button
                                                    onClick={() => removeStepAt(index)}
                                                    disabled={isRunning}
                                                >
                                                    X
                                                </button>
                                            </div>
                                        </div>

                                        <div className="sequence-step-grid">
                                            <div>
                                                <label>Button</label>
                                                <select
                                                    value={group.selectedButton}
                                                    onChange={(e) =>
                                                        updateStep(index, {
                                                            selectedButton: e.target.value,
                                                        })
                                                    }
                                                    className="sequence-step-grid-select"
                                                    disabled={!selectedRemote || isRunning}
                                                >
                                                    <option value="">Choose a button</option>
                                                    {availableButtons.map((button) => (
                                                        <option
                                                            key={button.value}
                                                            value={button.value}
                                                        >
                                                            {button.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label>Delay after step (ms)</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={50}
                                                    value={group.delay}
                                                    onChange={(e) =>
                                                        updateStep(index, {
                                                            delay: Number(e.target.value) || 0,
                                                        })
                                                    }
                                                    disabled={isRunning}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default ButtonsSequence;